import talk2Api from 'src/plugins/api/api'
import { isMaskedPhoneNumber, maskPhoneNumberToLastFour } from 'src/plugins/helpers/phone-masking'
import { receivesFullPhoneNumbers } from 'src/plugins/helpers/full-phone-numbers-channel'

// one request per communication: a call sends several updates within milliseconds, and a fetch that failed
// or came back masked would come back the same way. Bounded, since a session can stay open for days.
const fetched = new Map()
const MAX_FETCHED = 500

/**
 * The numbers of a call or text as the API sends them to this user, so in full for a user who sees them so.
 * @param {number} id the communication
 * @returns {Promise<{lead_number: string, contact_phone_number: string}|null>} null when the fetch failed
 */
export const fetchFullPhoneNumbers = (id) => {
  if (!fetched.has(id)) {
    if (fetched.size >= MAX_FETCHED) {
      fetched.delete(fetched.keys().next().value)
    }

    fetched.set(id, talk2Api.V1.communication.get(id)
      .then(({ data }) => ({ lead_number: data?.lead_number, contact_phone_number: data?.contact?.phone_number }))
      .catch(error => {
        console.log('fillFullPhoneNumbers: could not fetch communication', id, error)
        return null
      }))
  }

  return fetched.get(id)
}

/**
 * The number a ringing TaskRouter task is from, in full for a user who sees numbers so. The task carries it
 * masked for every worker it rings; the call's own number comes back from the API when it is the same one.
 * @param {number|string} communicationId the ringing call
 * @param {string} maskedNumber the number the task carries
 * @returns {Promise<string>} the full number, or the masked one when the fetch failed or found another number
 */
export const fetchFullCallerNumber = async (communicationId, maskedNumber) => {
  const numbers = await fetchFullPhoneNumbers(parseInt(communicationId, 10))
  const number = numbers?.lead_number

  return number && !isMaskedPhoneNumber(number) && maskPhoneNumberToLastFour(number) === maskedNumber ? number : maskedNumber
}

/**
 * A ringing task's caller number in full, read back for an Admin or Supervisor of an account that hides numbers.
 * The task carries it masked for every worker it rings.
 * @param {Object|null} task
 * @param {Object|null} company
 * @param {string[]|null} userRoles
 * @returns {Promise<{communicationId: number|string, number: string}|null>} null when the user sees the task's number
 */
export const loadFullCallerNumber = async (task, company, userRoles) => {
  const attributes = task?.attributes
  const maskedNumber = attributes?.contact_info?.phone

  if (!receivesFullPhoneNumbers(company, userRoles) || !isMaskedPhoneNumber(maskedNumber)) {
    return null
  }

  return { communicationId: attributes.communication_id, number: await fetchFullCallerNumber(attributes.communication_id, maskedNumber) }
}

/**
 * The number to show for a ringing task: the one loadFullCallerNumber loaded for its call, else the task's own.
 * @param {{communicationId: number|string, number: string}|null} loaded
 * @param {Object|null} task
 * @returns {string|undefined}
 */
export const knownCallerNumber = (loaded, task) => {
  const attributes = task?.attributes

  return loaded && String(loaded.communicationId) === String(attributes?.communication_id) ? loaded.number : attributes?.contact_info?.phone
}

/**
 * A company-wide update carries masked numbers, even to a user who sees them in full. When a list shows a
 * row with masked numbers, fetch its call or text, which the API sends this user in full, and put its
 * numbers on the row the list holds once they arrive: live updates replace the row object in the meantime.
 * Callers skip it for users who see numbers masked.
 * @param {number} id the communication the row shows
 * @param {function(number): (Object|undefined)} findRow the row a list holds now for a communication id
 * @returns {Promise<void>}
 */
export const fillFullPhoneNumbers = async (id, findRow) => {
  const isMasked = row => isMaskedPhoneNumber(row?.lead_number) || isMaskedPhoneNumber(row?.contact?.phone_number)

  if (!isMasked(findRow(id))) {
    return
  }

  const data = await fetchFullPhoneNumbers(id)
  const row = data && findRow(id)

  if (!row) {
    return
  }

  // a fetched number is kept for the session, so it must still be the one the row shows masked
  const isShownAs = (number, shown) => number && isMaskedPhoneNumber(shown) && maskPhoneNumberToLastFour(number) === shown

  if (isShownAs(data.lead_number, row.lead_number)) {
    row.lead_number = data.lead_number
  }

  if (isShownAs(data.contact_phone_number, row.contact?.phone_number)) {
    row.contact.phone_number = data.contact_phone_number
  }
}
